import type { Dispatch, SetStateAction } from "react";
import dayjs from "dayjs";
import { FiX } from "react-icons/fi";
import Datepicker from "react-tailwindcss-datepicker";
import FileTypeDropdown from "../dropdowns/fileTypeDropdown";
import { AdvancedSearchParams } from "./types";
import Modal from "../modal";
import { DATE_ONLY_FORMAT, getPresetDateRanges, resolveDateRange } from "../../utils/dateRanges";

const formatDateValue = (value: unknown) => {
  if (!value) return "";
  return dayjs(value as string | Date).format(DATE_ONLY_FORMAT);
};

type BrowseAdvancedSearchModalProps = {
  show: boolean;
  advancedSearchParams: AdvancedSearchParams;
  setAdvancedSearchParams: Dispatch<SetStateAction<AdvancedSearchParams>>;
  onClose: () => void;
  onReset: () => void;
  onSearch: () => void;
};

const BrowseAdvancedSearchModal = ({
  show,
  advancedSearchParams,
  setAdvancedSearchParams,
  onClose,
  onReset,
  onSearch,
}: BrowseAdvancedSearchModalProps) => {
  if (!show) return null;

  const presetRanges = getPresetDateRanges();
  const datepickerValue = (() => {
    const { startDate, endDate } = resolveDateRange(advancedSearchParams);
    return {
      startDate: startDate || null,
      endDate: endDate || null,
    };
  })();

  return (
    <Modal
      isOpen={show}
      onClose={onClose}
      panelClassName="rounded-lg shadow-xl max-w-2xl w-full mx-4 max-h-[80vh] flex flex-col bg-white overflow-visible"
    >
      <div className="flex items-center justify-between p-6 border-b border-gray-200">
        <h2 className="text-xl font-semibold">Advanced Search</h2>
        <button
          onClick={onClose}
          className="p-1 rounded-full transition-colors hover:bg-gray-100"
        >
          <FiX size={20} />
        </button>
      </div>
      <div className="flex-1 p-6 space-y-4" style={{ overflowY: "visible" }}>
        <div>
          <label className="block text-sm font-medium mb-2 text-gray-600">
            Item Name
          </label>
          <input
            type="text"
            value={advancedSearchParams.searchTerm}
            onChange={(e) =>
              setAdvancedSearchParams((prev) => ({
                ...prev,
                searchTerm: e.target.value,
              }))
            }
            placeholder="Search by name"
            className="w-full px-3 py-2 rounded-lg text-sm focus:outline-none focus:border-blue-500 bg-white border border-gray-300"
          />
        </div>
        <div>
          <label className="block text-sm font-medium mb-2 text-gray-600">
            Date Modified
          </label>
          <div className="relative w-full">
            <Datepicker
              primaryColor={"blue"}
              value={datepickerValue}
              onChange={(newValue) => {
                const startDate = formatDateValue(newValue?.startDate);
                const endDate = formatDateValue(newValue?.endDate);

                if (!startDate && !endDate) {
                  setAdvancedSearchParams((prev) => ({
                    ...prev,
                    dateModified: "any",
                    dateFrom: "",
                    dateTo: "",
                  }));
                  return;
                }

                const presetMatch = Object.entries(presetRanges).find(
                  ([, range]) =>
                    range.startDate === startDate && range.endDate === endDate
                );

                if (presetMatch) {
                  setAdvancedSearchParams((prev) => ({
                    ...prev,
                    dateModified: presetMatch[0],
                    dateFrom: "",
                    dateTo: "",
                  }));
                  return;
                }

                setAdvancedSearchParams((prev) => ({
                  ...prev,
                  dateModified: "custom",
                  dateFrom: startDate,
                  dateTo: endDate,
                }));
              }}
              placeholder={"Any time"}
              containerClassName="relative w-full text-gray-700 datepicker-range-white"
              inputClassName={
                "text-sm bg-white rounded-lg focus:ring-0 w-full py-2 px-3 border border-gray-300 focus:border-blue-500"
              }
              toggleClassName="text-gray-700 absolute right-0 h-full px-3 focus:outline-none"
              showShortcuts={true}
              popoverDirection="up"
              configs={{
                shortcuts: {
                  last7Days: {
                    text: "Last 7 days",
                    period: {
                      start: dayjs(presetRanges.last7Days?.startDate).toDate(),
                      end: dayjs(presetRanges.last7Days?.endDate).toDate(),
                    },
                  },
                  last30Days: {
                    text: "Last 30 days",
                    period: {
                      start: dayjs(presetRanges.last30Days?.startDate).toDate(),
                      end: dayjs(presetRanges.last30Days?.endDate).toDate(),
                    },
                  },
                  thisYear: {
                    text: "This year",
                    period: {
                      start: dayjs(presetRanges.thisYear?.startDate).toDate(),
                      end: dayjs(presetRanges.thisYear?.endDate).toDate(),
                    },
                  },
                  last3Years: {
                    text: "Last 3 years",
                    period: {
                      start: dayjs(presetRanges.last3Years?.startDate).toDate(),
                      end: dayjs(presetRanges.last3Years?.endDate).toDate(),
                    },
                  },
                },
              }}
            />
          </div>
        </div>
        <div>
          <label className="block text-sm font-medium mb-2 text-gray-600">
            File Types
          </label>
          <FileTypeDropdown
            dropdownProps={{
              selections: advancedSearchParams.fileTypes,
              onOptionToggle: (fileType: string) => {
                setAdvancedSearchParams((prev) => {
                  const isSelected = prev.fileTypes.includes(fileType);
                  return {
                    ...prev,
                    fileTypes: isSelected
                      ? prev.fileTypes.filter((t) => t !== fileType)
                      : [...prev.fileTypes, fileType],
                  };
                });
              },
              className: "w-full",
            }}
          />
        </div>
      </div>
      <div className="flex items-center justify-end space-x-3 p-6 border-t border-gray-200 bg-gray-50">
        <button
          onClick={onReset}
          className="px-4 py-2 rounded-lg text-sm transition-colors bg-gray-200 hover:bg-gray-300 text-gray-800"
        >
          Reset
        </button>
        <button
          onClick={onSearch}
          className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg text-sm transition-colors text-white"
        >
          Search
        </button>
      </div>
    </Modal>
  );
};

export default BrowseAdvancedSearchModal;
